import type { CslName, CslRef, EvidenceKind, ReferenceEvidence, ReferenceIndex } from './types';
import { hyphenParts, joined, normalizeToken, stripParticles, words } from './normalize';

/** Four-digit years this module treats as citation years. */
const YEAR_TOKEN_RE = /(?<!\d)(1[5-9]\d{2}|20\d{2})(?!\d)/u;
const YEAR_WITH_SUFFIX_RE = /(?<!\d)(?:1[5-9]\d{2}|20\d{2})([a-z])(?![\p{L}\p{N}])/u;

function yearOf(ref: CslRef): string | null {
  const issued = ref.issued;
  if (issued && typeof issued === 'object') {
    const dp = issued['date-parts'];
    if (Array.isArray(dp) && Array.isArray(dp[0]) && dp[0][0] != null) {
      const y = String(dp[0][0]).trim();
      if (YEAR_TOKEN_RE.test(y) && /^\d{4}$/.test(y)) return y;
    }
    for (const field of [issued.literal, issued.raw]) {
      if (typeof field === 'string') {
        const m = YEAR_TOKEN_RE.exec(field);
        if (m) return m[1];
      }
    }
  } else if (typeof issued === 'string') {
    const m = YEAR_TOKEN_RE.exec(issued);
    if (m) return m[1];
  }
  const raw = rawLine(ref);
  if (raw) {
    // The year of a bibliography line is the first one written in parentheses, or else the
    // first year-shaped token: a title may carry a year of its own, and it comes later.
    const paren = /\(\s*((?:1[5-9]|20)\d{2})[a-z]?\s*\)/u.exec(raw);
    if (paren) return paren[1];
    const m = YEAR_TOKEN_RE.exec(raw);
    if (m) return m[1];
  }
  return null;
}

function yearSuffixOf(ref: CslRef, year: string | null): string {
  const explicit = ref['year-suffix'];
  if (typeof explicit === 'string' && /^[a-z]$/u.test(explicit)) return explicit;
  const issued = ref.issued;
  const blobs: string[] = [];
  if (issued && typeof issued === 'object') {
    if (typeof issued.literal === 'string') blobs.push(issued.literal);
    if (typeof issued.raw === 'string') blobs.push(issued.raw);
  } else if (typeof issued === 'string') blobs.push(issued);
  const raw = rawLine(ref);
  if (raw) blobs.push(raw);
  for (const blob of blobs) {
    const m = YEAR_WITH_SUFFIX_RE.exec(blob);
    if (m && (!year || m[0].startsWith(year))) return m[1];
  }
  return '';
}

function rawLine(ref: CslRef): string | null {
  if (typeof ref.rawReference === 'string' && ref.rawReference.trim()) return ref.rawReference;
  if (typeof ref.sourceText === 'string' && ref.sourceText.trim()) return ref.sourceText;
  return null;
}

/** The part of the bibliography line before its year, split into normalized tokens. */
function rawHeadOf(ref: CslRef, year: string | null): { tokens: string[]; firstSegment: string[] } {
  const raw = rawLine(ref);
  if (!raw) return { tokens: [], firstSegment: [] };
  let head = raw;
  const cut = year ? raw.indexOf(year) : -1;
  if (cut > 0) head = raw.slice(0, cut);
  else {
    const paren = raw.indexOf('(');
    head = paren > 0 ? raw.slice(0, paren) : raw.slice(0, 200);
  }
  const tokens = words(head).map(normalizeToken).filter((t) => t.length > 0);
  // The first author ends at the first comma (`Meer, T., & Cho`) or sentence stop
  // (`Open Science Collaboration. (2015)`), whichever comes first.
  const stop = head.search(/,|\.\s|\(/u);
  const firstSegment = words(stop > 0 ? head.slice(0, stop) : head);
  return { tokens, firstSegment };
}

function authorsOf(ref: CslRef): CslName[] {
  if (Array.isArray(ref.author) && ref.author.length > 0) return ref.author.filter((a) => a && typeof a === 'object');
  if (Array.isArray(ref.editor) && ref.editor.length > 0) return ref.editor.filter((a) => a && typeof a === 'object');
  return [];
}

function put(map: Map<string, EvidenceKind>, form: string, kind: EvidenceKind): void {
  if (!form) return;
  // Keep the strongest kind already recorded for a form.
  const rank: Record<EvidenceKind, number> = {
    family: 3, literal: 3, 'particle-family': 3, 'given-family': 3, 'hyphen-part': 2, 'raw-head': 1,
  };
  const existing = map.get(form);
  if (!existing || rank[kind] > rank[existing]) map.set(form, kind);
}

/** Every written form one CSL name may take in a citation, with the evidence kind of each. */
function nameForms(name: CslName): Map<string, EvidenceKind> {
  const forms = new Map<string, EvidenceKind>();
  const family = typeof name.family === 'string' ? name.family.trim() : '';
  const literal = typeof name.literal === 'string' ? name.literal.trim() : '';
  const given = typeof name.given === 'string' ? name.given.trim() : '';
  const particles = [name.particle, name['non-dropping-particle'], name['dropping-particle']]
    .filter((p): p is string => typeof p === 'string' && p.trim().length > 0)
    .map((p) => p.trim());

  if (family) {
    const fw = words(family);
    put(forms, joined(fw), 'family');
    put(forms, joined(stripParticles(fw)), 'family');
    for (const p of particles) put(forms, joined(words(`${p} ${family}`)), 'particle-family');
    if (given) put(forms, joined(words(`${given} ${family}`)), 'given-family');
    for (const w of fw) for (const part of hyphenParts(w)) put(forms, normalizeToken(part), 'hyphen-part');
  }
  if (literal) {
    const lw = words(literal);
    put(forms, joined(lw), 'literal');
    put(forms, joined(stripParticles(lw)), 'literal');
    for (const w of lw) for (const part of hyphenParts(w)) put(forms, normalizeToken(part), 'hyphen-part');
  }
  return forms;
}

function givenTokens(name: CslName): string[] {
  const given = typeof name.given === 'string' ? name.given : '';
  return given
    .split(/[^\p{L}\p{M}\p{N}]+/u)
    .map(normalizeToken)
    .filter((t) => t.length > 0);
}

/**
 * Build the evidence index for a reference list. Each reference contributes the forms its
 * first author and its co-authors may be written as, its year and suffix, its author count and
 * the tokens of its raw bibliography line before the year. `byFamilyYear` records every
 * reference claiming a `first-author-form|year` key, so a collision two references share is
 * visible to the resolver instead of being decided by list order.
 */
export function indexReferences(refs: CslRef[]): ReferenceIndex {
  const index: ReferenceIndex = { refs: new Map(), byFamilyYear: new Map() };
  for (const ref of refs) {
    if (!ref || typeof ref !== 'object') continue;
    const id = ref.id === undefined || ref.id === null ? '' : String(ref.id);
    if (!id) continue;

    const year = yearOf(ref);
    const yearSuffix = yearSuffixOf(ref, year);
    const authors = authorsOf(ref);
    const { tokens: rawHead, firstSegment } = rawHeadOf(ref, year);

    const firstAuthor = new Map<string, EvidenceKind>();
    const all = new Map<string, EvidenceKind>();
    const given = new Set<string>();

    authors.forEach((name, i) => {
      const forms = nameForms(name);
      for (const [form, kind] of forms) {
        put(all, form, kind);
        if (i === 0) put(firstAuthor, form, kind);
      }
      for (const g of givenTokens(name)) given.add(g);
    });

    if (firstSegment.length > 0) {
      put(firstAuthor, joined(firstSegment), 'raw-head');
      put(firstAuthor, joined(stripParticles(firstSegment)), 'raw-head');
    }
    for (const t of rawHead) put(all, t, 'raw-head');

    const evidence: ReferenceEvidence = {
      id,
      firstAuthor,
      authors: all,
      given,
      rawHead,
      year,
      yearSuffix,
      authorCount: authors.length,
    };
    index.refs.set(id, evidence);

    if (year) {
      for (const [form, kind] of firstAuthor) {
        if (kind === 'raw-head' || kind === 'hyphen-part') continue;
        const key = `${form}|${year}`;
        let ids = index.byFamilyYear.get(key);
        if (!ids) index.byFamilyYear.set(key, (ids = new Set()));
        ids.add(id);
      }
    }
  }
  return index;
}
